import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Box } from '@lightspeed/flame/Core';

import { toBoolean } from 'root/apps/pages/utils/sanitize';
import { ErrorMessageContext } from 'root/apps/PurchaseOrder/ShipmentHistory/contexts/ErrorMessage';
import { OverviewDataContext } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';

import Header from './components/Header';
import Information from './components/Information';
import Options from './components/Options';
import Overview from './components/Overview';
import ShipmentItems from './components/ShipmentItems';

import type { Page } from '..';

interface Props {
  setPage: (page: Page) => void;
}

const ShipmentHistoryView: React.FC<Props> = ({ setPage }) => {
  const { error, setError } = React.useContext(ErrorMessageContext);
  const { data: overviewData, getOverviewData } = React.useContext(OverviewDataContext);
  const [hasProductCostPermission] = React.useState<boolean | null>(
    toBoolean(window.merchantos.session.sessData?.Rights?.product_cost)
  );

  const { id: orderId, shipment_id: shipmentId } = window.history.getFilters();

  const { currency_code: currencyCode } = window.merchantos.locale;
  const isMultiCurrency = overviewData ? overviewData?.vendorCurrencyCode !== currencyCode : false;

  React.useEffect(() => {
    const overrideShipmentTabLink = () => {
      window.history.pushState(
        null,
        '',
        `/?name=purchase.views.purchase&form_name=view&id=${orderId}`
      );
      setPage('listing');
    };

    document
      .getElementById('menuShipmentHistory')!
      .addEventListener('click', overrideShipmentTabLink);

    return () => {
      document
        .getElementById('menuShipmentHistory')
        ?.removeEventListener('click', overrideShipmentTabLink);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setError(null);
    getOverviewData({ orderId: orderId as number, shipmentId: shipmentId as number });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeErrorMessage = () => {
    setError(null);
  };

  return (
    <Box p={4} backgroundColor="gray-50" style={{ fontSize: '14px' }}>
      {error ? (
        <Alert
          type="danger"
          title={error.title}
          data-test="api-error-alert"
          mb={2}
          onClose={closeErrorMessage}
        >
          {error.message}
        </Alert>
      ) : null}

      <Header />
      <Options />
      <Information isMultiCurrency={isMultiCurrency} />

      {hasProductCostPermission ? <Overview isMultiCurrency={isMultiCurrency} /> : null}

      <ShipmentItems
        isMultiCurrency={isMultiCurrency}
        vendorCurrencyCode={overviewData?.vendorCurrencyCode}
      />
    </Box>
  );
};

export default ShipmentHistoryView;
